import { useMemo } from 'react';
import { CANNOT_DELETE_EVENT, TYPE_BEAT_EVENT_ENUM, TYPE_RHYTHM_EVENT_ENUM } from '@/shared/config';

import {
  buildGroupEventFilterSnapshot,
  isAppliedFilterEvent,
  isAppliedFilterSpecialEvent,
  updateGroupEvents,
} from '@/entities/event';
import {
  DEFAULT_CONTROL_MODAL_VALUE,
  selectEventChanges,
  selectEventFilter,
  selectGroupEventChanges,
  selectHolterEvents,
  useReportStore,
} from '@/entities/report';
import type { BulkActionByEventProps } from './index';

/**
 * Backs the bulk-action dropdown of an event tab, supplying its filter state, its menu entries and the handler applying a chosen action. Reads the tab's grouped event changes, event changes, event filter, Holter events and special event types from the report store, memoizes the menu on the active button and the dirty flag, and derives whether the special-event and event filters are currently applied.
 *
 * @param props - The hook input carrying keyRecoil, activeButton and hasChanged.
 * @returns An object carrying eventFilter, holterEvents, bulkActionMenu, isFilteredSpecialEvent, isFilteredEvents and onClickItem.
 */
const useBulkActionByEvent = ({
  keyRecoil,
  activeButton,
  hasChanged,
}: Pick<BulkActionByEventProps, 'keyRecoil' | 'activeButton' | 'hasChanged'>) => {
  const groupEventChanges = useReportStore(selectGroupEventChanges(keyRecoil));
  const setGroupEventChangesAction = useReportStore((s) => s.actions.setGroupEventChanges);
  /**
   * Writes the grouped event edits for the tab this hook is bound to. Forwards the value to the store's setGroupEventChanges action alongside the hook's keyRecoil so the caller never passes the tab key.
   *
   * @param v - The next value, or an updater receiving the previous one.
   * @returns Whatever the store's setGroupEventChanges action returns.
   */
  const setGroupEventChanges = (v: Parameters<typeof setGroupEventChangesAction>[1]) =>
    setGroupEventChangesAction(keyRecoil, v);
  const eventChanges = useReportStore(selectEventChanges(keyRecoil));
  const setEventChangesAction = useReportStore((s) => s.actions.setEventChanges);
  /**
   * Writes the pending event edits for the tab this hook is bound to. Forwards the value to the store's setEventChanges action alongside the hook's keyRecoil so the caller never passes the tab key.
   *
   * @param v - The next value, or an updater receiving the previous one.
   * @returns Whatever the store's setEventChanges action returns.
   */
  const setEventChanges = (v: Parameters<typeof setEventChangesAction>[1]) =>
    setEventChangesAction(keyRecoil, v);
  const eventFilter = useReportStore(selectEventFilter(keyRecoil));
  const holterEvents = useReportStore(selectHolterEvents(keyRecoil));
  const specialEventTypes = useReportStore((s) => s.specialEventTypes);
  const setEventOptionsAction = useReportStore((s) => s.actions.setEventOptions);
  /**
   * Writes the event display options for the tab this hook is bound to. Forwards the value to the store's setEventOptions action alongside the hook's keyRecoil so the caller never passes the tab key.
   *
   * @param v - The next value, or an updater receiving the previous one.
   * @returns Whatever the store's setEventOptions action returns.
   */
  const setEventOptions = (v: Parameters<typeof setEventOptionsAction>[1]) =>
    setEventOptionsAction(keyRecoil, v);
  const setControlModalAction = useReportStore((s) => s.actions.setControlModal);
  /**
   * Writes the control modal state for the tab this hook is bound to. Forwards the value to the store's setControlModal action alongside the hook's keyRecoil so the caller never passes the tab key.
   *
   * @param v - The next value, or an updater receiving the previous one.
   * @returns Whatever the store's setControlModal action returns.
   */
  const setControlModal = (v: Parameters<typeof setControlModalAction>[1]) =>
    setControlModalAction(keyRecoil, v);

  /**
   * Builds the bulk-action menu offered for the event type currently active on the tab. Adds a conversion entry for VE Run, SVE Run, VT or SVT, always offers marking all as artifact, reviewed and unreviewed, includes delete unless the active type sits in CANNOT_DELETE_EVENT, and appends a reset entry when there are unsaved changes.
   *
   * @returns The menu entries, each carrying a label and a value.
   */
  const getBulkActionMenu = () => {
    const menu = [];

    if (activeButton === TYPE_BEAT_EVENT_ENUM.VE_RUN) {
      menu.push({ label: 'Change all events to VT', value: 'changeAllToVT' });
    }
    if (activeButton === TYPE_BEAT_EVENT_ENUM.SVE_RUN) {
      menu.push({ label: 'Change all events to SVT', value: 'changeAllToSVT' });
    }
    if (activeButton === TYPE_RHYTHM_EVENT_ENUM.VT) {
      menu.push({ label: 'Change all events to VE Run', value: 'changeAllToVE' });
    }
    if (activeButton === TYPE_RHYTHM_EVENT_ENUM.SVT) {
      menu.push({ label: 'Change all events to SVE Run', value: 'changeAllToSVE' });
    }

    menu.push({ label: 'Mark all as artifact', value: 'markedAsArtifact' });

    if (!CANNOT_DELETE_EVENT.includes(activeButton)) {
      menu.push({ label: 'Delete all events', value: 'delete' });
    }

    menu.push(
      { label: 'Mark all as reviewed', value: 'markedAsReviewed' },
      { label: 'Mark all as unreviewed', value: 'markedAsUnreviewed' },
    );

    if (hasChanged) {
      menu.push({ label: 'Reset all changes', value: 'resetAllChanges' });
    }
    return menu;
  };

  const bulkActionMenu = useMemo(() => getBulkActionMenu(), [activeButton, hasChanged]);
  const isFilteredSpecialEvent = isAppliedFilterSpecialEvent(specialEventTypes, activeButton);
  const isFilteredEvents = isAppliedFilterEvent(eventFilter, activeButton);
  /**
   * Applies the bulk action picked from the dropdown to the tab's grouped events. Snapshots the event and special-event filters, runs updateGroupEvents for the active button and the picked action, stores the result, mirrors the existing event changes into both the event options and the event changes, then closes the control modal.
   *
   * @param key - The picked menu entry's value, naming the bulk action to apply.
   * @returns Nothing.
   */
  const onClickItem = ({ key }: { key: any }) => {
    const action = key;
    const filterSnapshot = buildGroupEventFilterSnapshot(eventFilter, specialEventTypes);
    const updatedGroupEvents = updateGroupEvents(
      activeButton,
      action,
      groupEventChanges,
      filterSnapshot,
    );
    setGroupEventChanges(updatedGroupEvents);
    setEventOptions((prevOptions: any) => ({
      ...prevOptions,
      eventChangesState: eventChanges,
    }));
    setEventChanges(eventChanges);
    setControlModal(DEFAULT_CONTROL_MODAL_VALUE);
  };

  return {
    eventFilter,
    holterEvents,
    bulkActionMenu,
    isFilteredSpecialEvent,
    isFilteredEvents,
    onClickItem,
  };
};

export default useBulkActionByEvent;
